import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { CircularProgress, Box } from '@mui/material';
import DataTableFilters from './DataTableFilters';
import DataTablePagination from './DataTablePagination';
import DataTableViewDesktop from './DataTableViewDesktop';
import DataTableViewMobile from './DataTableViewMobile';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const DataTable = ({
  data = [],
  loading = false,
  error = '',
  totalItems = 0,
  title = '',
  subtitle = '',
  columns = [],
  mobileCardRenderer = null,
  headerActions = null,
  rowActions = null,
  mobileActions = null,
  extraColumns = [],
  filterConfig = [],
  onFilterChange = null,
  onRefresh = null,
  externalPagination = null,
  onPageChange = null,
  onItemsPerPageChange = null,
  onSortChange = null,
  className = '',
  tableClassName = '',
  searchPlaceholder = 'Buscar...',
  initialItemsPerPage = 10,
  snackbar = { open: false, text: '', severity: 'success' },
  onCloseSnackbar = null,
  searchValue = '',
  onSearchChange = null,
  filterValues = {},
  onClearFilters = null,
  itemsPerPageValue = null,
  currentPageValue = null,
  useServerPagination = false,
  sortField = null,
  sortDirection = null,
  customFilterFunction = null,
}) => {
  const [internalSearchText, setInternalSearchText] = useState('');
  const [internalFilters, setInternalFilters] = useState({});
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalItemsPerPage, setInternalItemsPerPage] = useState(initialItemsPerPage);
  const [isSearching, setIsSearching] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const [internalSortConfig, setInternalSortConfig] = useState({
    field: sortField || null,
    direction: sortDirection || 'desc'
  });

  const searchText = onSearchChange ? searchValue : internalSearchText;
  const filters = onFilterChange ? filterValues : internalFilters;
  const currentPage = externalPagination?.currentPage !== undefined
    ? externalPagination.currentPage
    : (onPageChange ? currentPageValue : internalCurrentPage);
  const itemsPerPage = onItemsPerPageChange ? itemsPerPageValue : internalItemsPerPage;
  const sortConfig = onSortChange
    ? { field: sortField, direction: sortDirection }
    : internalSortConfig;

  useEffect(() => {
    if (!useServerPagination) {
      setFilteredData(data);
    }
  }, [data, useServerPagination]);

  const sortData = useCallback((dataToSort, sortKey, direction) => {
    if (!dataToSort.length) return [];

    const column = columns.find(col => col.key === sortKey);
    const sorted = [...dataToSort];

    sorted.sort((a, b) => {
      let aValue, bValue;

      if (column?.sortValue) {
        aValue = column.sortValue(a);
        bValue = column.sortValue(b);
        if (aValue) aValue = new Date(aValue);
        if (bValue) bValue = new Date(bValue);
      } else {
        switch (sortKey) {
          case 'fecha_inicio':
            aValue = a.fecha_inicio && a.fecha_inicio !== '—' ? new Date(a.fecha_inicio) : null;
            bValue = b.fecha_inicio && b.fecha_inicio !== '—' ? new Date(b.fecha_inicio) : null;
            break;
          case 'fecha_finalizacion':
            aValue = (a.estado === 'finalizado' && a.fecha_finalizacion && a.fecha_finalizacion !== '—')
              ? new Date(a.fecha_finalizacion) : null;
            bValue = (b.estado === 'finalizado' && b.fecha_finalizacion && b.fecha_finalizacion !== '—')
              ? new Date(b.fecha_finalizacion) : null;
            break;
          default:
            return 0;
        }
      }

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [columns]);

  useEffect(() => {
    if (!useServerPagination && data.length) {
      let filtered = [...data];

      if (searchText) {
        const searchLower = searchText.toLowerCase();
        filtered = filtered.filter(item =>
          columns.some(col => {
            if (col.searchable === false) return false;
            const value = item[col.key];
            return value && String(value).toLowerCase().includes(searchLower);
          })
        );
      }

      if (customFilterFunction) {
        filtered = filtered.filter(item => customFilterFunction(item, filters));
      } else {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            filtered = filtered.filter(item => item[key] === value);
          }
        });
      }

      if (sortConfig.field) {
        filtered = sortData(filtered, sortConfig.field, sortConfig.direction);
      }

      setFilteredData(filtered);
      setIsSearching(false);
    }
  }, [data, searchText, filters, columns, useServerPagination, sortConfig, sortData, customFilterFunction]);

  useEffect(() => {
    if (useServerPagination) {
      setFilteredData(data);
      setIsSearching(false);
    }
  }, [data, useServerPagination]);

  const handleSort = useCallback((key) => {
    const column = columns.find(col => col.key === key);
    if (!column?.sortable) return;

    if (onSortChange) {
      const newDirection = sortConfig.field === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
      onSortChange(key, newDirection);
    } else {
      setInternalSortConfig(prevConfig => ({
        field: key,
        direction: prevConfig.field === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
      }));
    }
  }, [columns, onSortChange, sortConfig]);

  const handleClearAllFilters = useCallback(() => {
    if (onClearFilters) {
      onClearFilters();
    } else {
      setInternalSearchText('');
      setInternalFilters({});
      setInternalSortConfig({ field: null, direction: 'desc' });
      setInternalCurrentPage(1);
    }
  }, [onClearFilters]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchText(value);
    }
  };

  const handleFilterChange = (key, value) => {
    if (onFilterChange) {
      onFilterChange(key, value);
    } else {
      setInternalFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handlePageChange = (page) => {
    if (externalPagination?.onPageChange) {
      externalPagination.onPageChange(page);
    } else if (onPageChange) {
      onPageChange(page);
    } else {
      setInternalCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (externalPagination?.onItemsPerPageChange) {
      externalPagination.onItemsPerPageChange(newItemsPerPage);
    } else if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage);
    } else {
      setInternalItemsPerPage(newItemsPerPage);
      setInternalCurrentPage(1);
    }
  };

  useEffect(() => {
    if (!onSearchChange && !onFilterChange && !useServerPagination) {
      setInternalCurrentPage(1);
    }
  }, [internalSearchText, internalFilters, internalItemsPerPage, sortConfig, onSearchChange, onFilterChange, useServerPagination]);

  const paginatedData = useMemo(() => {
    if (useServerPagination) return filteredData;
    if (!filteredData.length) return [];
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage, useServerPagination]);

  const totalPages = useServerPagination
    ? (externalPagination?.totalPages || Math.ceil(totalItems / itemsPerPage))
    : Math.ceil(filteredData.length / itemsPerPage);

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container fluid className="px-4 py-4">
        <Alert variant="danger" onClose={() => { }} dismissible>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className={`px-4 py-3 border-bottom ${className}`}>
        <Row className="align-items-center">
          <Col>
            <h3 className="mb-0" style={{ color: '#005793' }}>{title}</h3>
            {subtitle && (
              <small className="text-muted">
                {subtitle} ({useServerPagination ? totalItems : filteredData.length} de {totalItems || data.length})
              </small>
            )}
          </Col>
          {headerActions && <Col xs="auto">{headerActions}</Col>}
        </Row>
      </Container>

      <DataTableFilters
        searchText={searchText}
        onSearchChange={handleSearchChange}
        isSearching={isSearching}
        searchPlaceholder={searchPlaceholder}
        filterConfig={filterConfig}
        filters={filters}
        onFilterChange={handleFilterChange}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onClearFilters={handleClearAllFilters}
      />

      <Container fluid className="px-4">
        <DataTableViewDesktop
          data={paginatedData}
          columns={columns}
          rowActions={rowActions}
          extraColumns={extraColumns}
          tableClassName={tableClassName}
          sortConfig={sortConfig}
          onSort={handleSort}
        />

        <DataTableViewMobile
          data={paginatedData}
          mobileCardRenderer={mobileCardRenderer}
          mobileActions={mobileActions}
        />

        {filteredData.length > 0 && totalPages > 1 && (
          <DataTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Container>

      {onCloseSnackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={onCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert onClose={onCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.text}
          </MuiAlert>
        </Snackbar>
      )}
    </>
  );
};

export default React.memo(DataTable);